<template>
  <div class="tree-project-explorer">
    <v-toolbar density="compact" color="primary" class="explorer-header">
      <v-toolbar-title class="text-body-2">
        Explorador de Projetos
      </v-toolbar-title>
      
      <v-spacer></v-spacer>
      
      <v-btn
        icon
        variant="text"
        size="small"
        @click="addNewProject"
        v-tooltip="'Novo Projeto'"
      >
        <v-icon>mdi-folder-plus</v-icon>
      </v-btn>
      
      <v-btn
        icon
        variant="text"
        size="small"
        @click="addNewFile"
        v-tooltip="'Novo Arquivo'"
      >
        <v-icon>mdi-file-plus</v-icon>
      </v-btn>
    </v-toolbar>
    
    <div v-if="loading" class="d-flex align-center justify-center pa-4">
      <v-progress-circular indeterminate color="primary" size="24" />
      <span class="ml-2">Carregando projetos...</span>
    </div>
    
    <div v-else-if="projects.length === 0" class="d-flex flex-column align-center justify-center empty-state pa-4">
      <v-icon size="36" color="grey-lighten-1" class="mb-2">mdi-folder-open</v-icon>
      <div class="text-subtitle-2 text-grey">Nenhum projeto</div>
      <v-btn
        variant="text"
        color="primary"
        size="small"
        prepend-icon="mdi-plus"
        class="mt-2"
        @click="addNewProject"
      >
        Criar projeto
      </v-btn>
    </div>
    
    <div v-else class="projects-container">
      <div v-for="project in projects" :key="project.id" class="project-item mb-2">
        <div class="project-header d-flex align-center px-2 py-2 rounded-t">
          <v-icon size="small" color="primary" class="mr-2">{{ project.expanded ? 'mdi-folder-open' : 'mdi-folder' }}</v-icon>
          <span class="text-subtitle-2 font-weight-medium">{{ project.name }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            variant="text"
            size="x-small"
            @click="toggleProject(project)"
          >
            <v-icon>{{ project.expanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
          </v-btn>
        </div>
        
        <transition name="fade">
          <div v-if="project.expanded" class="project-files">
            <v-list density="compact" class="pa-0">
              <v-list-item
                v-for="file in project.files"
                :key="file.id"
                @click="selectFile(file)"
                :active="isFileActive(file)"
                color="primary"
                class="file-item"
                :prepend-icon="getFileIcon(file)"
              >
                <v-list-item-title class="d-flex align-center">
                  <span class="file-name" :class="{'text-primary': isFileActive(file)}">{{ file.name }}</span>
                  <div class="file-actions ml-auto">
                    <v-btn icon="mdi-pencil" size="x-small" variant="text" @click.stop="renameFile(file)"></v-btn>
                    <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click.stop="confirmDeleteFile(file)"></v-btn>
                  </div>
                </v-list-item-title>
              </v-list-item>
              
              <v-list-item v-if="project.files.length === 0" class="text-grey text-center py-2">
                <v-list-item-title>Nenhum arquivo</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>
        </transition>
      </div>
    </div>
    
    <!-- Diálogo para criar novo projeto -->
    <v-dialog v-model="newProjectDialog.show" width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">Novo Projeto</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newProjectDialog.name"
            label="Nome do Projeto"
            variant="outlined"
            hide-details="auto"
            autofocus
            @keyup.enter="createProject"
          ></v-text-field>
          
          <div class="text-subtitle-2 mt-4 mb-2">Modelo:</div>
          <v-card class="mb-3" :color="newProjectDialog.template === 'empty' ? 'primary-lighten-5' : undefined" @click="newProjectDialog.template = 'empty'">
            <v-card-text class="d-flex align-center">
              <v-icon class="mr-2" :color="newProjectDialog.template === 'empty' ? 'primary' : undefined">mdi-file-outline</v-icon>
              <div>
                <div class="text-body-1">Projeto Vazio</div>
                <div class="text-caption">Começar do zero</div>
              </div>
              <v-spacer></v-spacer>
              <v-radio-group v-model="newProjectDialog.template" hide-details density="compact">
                <v-radio value="empty"></v-radio>
              </v-radio-group>
            </v-card-text>
          </v-card>
          
          <v-card :color="newProjectDialog.template === 'token' ? 'primary-lighten-5' : undefined" @click="newProjectDialog.template = 'token'">
            <v-card-text class="d-flex align-center">
              <v-icon class="mr-2" :color="newProjectDialog.template === 'token' ? 'primary' : undefined">mdi-currency-usd</v-icon>
              <div>
                <div class="text-body-1">Token QRC-21</div>
                <div class="text-caption">Implementação básica do padrão QRC-21</div>
              </div>
              <v-spacer></v-spacer>
              <v-radio-group v-model="newProjectDialog.template" hide-details density="compact">
                <v-radio value="token"></v-radio>
              </v-radio-group>
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="newProjectDialog.show = false">Cancelar</v-btn>
          <v-btn color="primary" variant="flat" @click="createProject" :disabled="!newProjectDialog.name">Criar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Diálogo para novo arquivo -->
    <v-dialog v-model="newFileDialog.show" width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">Novo Arquivo</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newFileDialog.name"
            label="Nome do Arquivo"
            variant="outlined"
            hint="Inclua a extensão (ex: Contract.quartz)"
            persistent-hint
            hide-details="auto"
            autofocus
            @keyup.enter="createFile"
          ></v-text-field>
          
          <v-select
            v-if="projects.length > 1"
            v-model="newFileDialog.projectId"
            :items="projects" 
            item-title="name"
            item-value="id"
            label="Projeto"
            variant="outlined"
            class="mt-3"
            hide-details="auto"
          ></v-select>
          
          <div v-else class="mt-3 text-body-2">
            Projeto: <span class="font-weight-medium">{{ projects.length > 0 ? projects[0].name : 'Padrão' }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="newFileDialog.show = false">Cancelar</v-btn>
          <v-btn color="primary" variant="flat" @click="createFile" :disabled="!newFileDialog.name">Criar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Diálogo para renomear arquivo -->
    <v-dialog v-model="renameDialog.show" width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">Renomear Arquivo</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="renameDialog.newName"
            label="Novo Nome"
            variant="outlined"
            hide-details="auto"
            autofocus
            @keyup.enter="doRenameFile"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="renameDialog.show = false">Cancelar</v-btn>
          <v-btn color="primary" variant="flat" @click="doRenameFile" :disabled="!renameDialog.newName">Renomear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Diálogo para confirmar exclusão -->
    <v-dialog v-model="deleteDialog.show" width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">Excluir Arquivo</v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir <strong>{{ deleteDialog.file ? deleteDialog.file.name : '' }}</strong>?
          <div class="text-caption text-error mt-2">Esta ação não pode ser desfeita.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog.show = false">Cancelar</v-btn>
          <v-btn color="error" variant="flat" @click="doDeleteFile">Excluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Função simples para gerar IDs
function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}

// Conteúdo padrão para token QRC-21
const TOKEN_QRC21_TEMPLATE = `// Implementação do Token QRC-21 (Padrão QuarnX)
// Autor: Sistema QuarnX
// Data: ${new Date().toISOString().split('T')[0]}

contract TokenQRC21 {
  // Propriedades do token
  private string name;
  private string symbol;
  private uint8 decimals;
  private uint256 totalSupply;
  
  // Mapeamentos para saldos e permissões
  private mapping(address => uint256) balances;
  private mapping(address => mapping(address => uint256)) allowed;
  
  // Eventos emitidos pelo contrato
  event Transfer(address indexed from, address indexed to, uint256 value);
  event Approval(address indexed owner, address indexed spender, uint256 value);

  /**
   * Construtor do token
   * @param _name Nome do token
   * @param _symbol Símbolo do token
   * @param _decimals Casas decimais
   * @param _initialSupply Fornecimento inicial
   */
  constructor(
    string memory _name,
    string memory _symbol,
    uint8 _decimals,
    uint256 _initialSupply
  ) {
    name = _name;
    symbol = _symbol;
    decimals = _decimals;
    totalSupply = _initialSupply * 10 ** uint256(decimals);
    balances[msg.sender] = totalSupply;
    
    // Emite evento de transferência inicial (do endereço zero)
    emit Transfer(address(0), msg.sender, totalSupply);
  }

  // Retorna o nome do token
  function getName() public view returns (string memory) {
    return name;
  }

  // Retorna o símbolo do token
  function getSymbol() public view returns (string memory) {
    return symbol;
  }

  // Retorna o número de casas decimais
  function getDecimals() public view returns (uint8) {
    return decimals;
  }

  // Retorna o fornecimento total do token
  function getTotalSupply() public view returns (uint256) {
    return totalSupply;
  }

  // Retorna o saldo de um endereço
  function balanceOf(address _owner) public view returns (uint256) {
    return balances[_owner];
  }

  /**
   * Transfere tokens para um endereço
   * @param _to Endereço de destino
   * @param _value Quantidade de tokens
   * @return Sucesso da operação
   */
  function transfer(address _to, uint256 _value) public returns (bool) {
    require(_to != address(0), "Endereço inválido");
    require(_value <= balances[msg.sender], "Saldo insuficiente");

    balances[msg.sender] -= _value;
    balances[_to] += _value;
    
    emit Transfer(msg.sender, _to, _value);
    return true;
  }

  /**
   * Aprova um endereço para gastar tokens em seu nome
   * @param _spender Endereço autorizado
   * @param _value Quantidade de tokens
   * @return Sucesso da operação
   */
  function approve(address _spender, uint256 _value) public returns (bool) {
    require(_spender != address(0), "Endereço inválido");
    
    allowed[msg.sender][_spender] = _value;
    emit Approval(msg.sender, _spender, _value);
    return true;
  }

  /**
   * Retorna a quantidade de tokens aprovados para um spender
   * @param _owner Dono dos tokens
   * @param _spender Endereço autorizado
   * @return Quantidade de tokens aprovados
   */
  function allowance(address _owner, address _spender) public view returns (uint256) {
    return allowed[_owner][_spender];
  }

  /**
   * Transfere tokens de um endereço para outro
   * @param _from Endereço de origem
   * @param _to Endereço de destino
   * @param _value Quantidade de tokens
   * @return Sucesso da operação
   */
  function transferFrom(address _from, address _to, uint256 _value) public returns (bool) {
    require(_from != address(0), "Endereço de origem inválido");
    require(_to != address(0), "Endereço de destino inválido");
    require(_value <= balances[_from], "Saldo insuficiente");
    require(_value <= allowed[_from][msg.sender], "Permissão insuficiente");

    balances[_from] -= _value;
    balances[_to] += _value;
    allowed[_from][msg.sender] -= _value;
    
    emit Transfer(_from, _to, _value);
    return true;
  }

  // Extensões QRC-21
  
  /**
   * Transferência entre blockchains
   * @param _network Rede de destino
   * @param _address Endereço na rede de destino
   * @param _value Quantidade de tokens
   * @return Sucesso da operação
   */
  function crossChainTransfer(
    string memory _network,
    string memory _address,
    uint256 _value
  ) public returns (bool) {
    // Implementação de transferência cross-chain
    // Esta é uma funcionalidade específica da QuarnX
    
    // Código de implementação...
    
    return true;
  }
}`;

export default {
  name: 'TreeProjectExplorer',
  emits: ['file-selected'],
  
  data() {
    return {
      loading: false,
      projects: [],
      activeFile: null,
      
      // Diálogos
      newProjectDialog: {
        show: false,
        name: '',
        template: 'empty'
      },
      
      newFileDialog: {
        show: false,
        name: '',
        projectId: ''
      },
      
      renameDialog: {
        show: false,
        file: null,
        newName: ''
      },
      
      deleteDialog: {
        show: false,
        file: null
      }
    };
  },
  
  mounted() {
    this.loadProjects();
  },
  
  methods: {
    // Carregamento de dados
    loadProjects() {
      this.loading = true;
      
      // Simulando carregamento
      setTimeout(() => {
        // Dados de exemplo
        this.projects = [
          {
            id: 'project-1',
            name: 'Meu Primeiro Projeto',
            expanded: true,
            files: [
              {
                id: 'file-1',
                name: 'TokenQRC21.quartz',
                type: 'quartz',
                content: TOKEN_QRC21_TEMPLATE
              },
              {
                id: 'file-2',
                name: 'README.md',
                type: 'markdown',
                content: '# Projeto QRC-21\n\nEste é um exemplo de implementação do padrão QRC-21 na linguagem Quartz.\n\n## Características\n\n- Compatível com o padrão QuarnX para tokens fungíveis\n- Suporte para transferências cross-chain\n- Proteção contra reentrância'
              }
            ]
          }
        ];
        
        this.loading = false;
      }, 500);
    },
    
    // Interação com projetos e arquivos
    toggleProject(project) {
      project.expanded = !project.expanded;
    },
    
    selectFile(file) {
      this.activeFile = file;
      this.$emit('file-selected', file);
    },
    
    isFileActive(file) {
      return this.activeFile && this.activeFile.id === file.id;
    },
    
    // Iconografia
    getFileIcon(file) {
      if (!file) return 'mdi-file';
      
      const extension = file.name.split('.').pop().toLowerCase();
      
      switch (extension) {
        case 'quartz':
          return 'mdi-language-typescript';
        case 'js':
          return 'mdi-language-javascript';
        case 'json':
          return 'mdi-code-json';
        case 'md':
          return 'mdi-language-markdown';
        case 'txt':
          return 'mdi-file-document';
        default:
          return 'mdi-file';
      }
    },
    
    // Ações de CRUD
    addNewProject() {
      this.newProjectDialog.name = '';
      this.newProjectDialog.template = 'token'; // Default to token template
      this.newProjectDialog.show = true;
    },
    
    createProject() {
      if (!this.newProjectDialog.name.trim()) return;
      
      const projectId = generateId();
      const newProject = {
        id: projectId,
        name: this.newProjectDialog.name,
        expanded: true,
        files: []
      };
      
      // Adicionar arquivos conforme o template
      if (this.newProjectDialog.template === 'token') {
        newProject.files.push({
          id: generateId(),
          name: 'TokenQRC21.quartz',
          type: 'quartz',
          content: TOKEN_QRC21_TEMPLATE
        });
        
        newProject.files.push({
          id: generateId(),
          name: 'README.md',
          type: 'markdown',
          content: `# ${this.newProjectDialog.name}\n\nImplementação de token QRC-21 utilizando a linguagem Quartz.`
        });
      }
      
      this.projects.push(newProject);
      this.newProjectDialog.show = false;
    },
    
    addNewFile() {
      if (this.projects.length === 0) {
        this.addNewProject();
        return;
      }
      
      this.newFileDialog.name = '';
      this.newFileDialog.projectId = this.projects[0].id;
      this.newFileDialog.show = true;
    },
    
    createFile() {
      if (!this.newFileDialog.name.trim()) return;
      
      const projectId = this.newFileDialog.projectId || (this.projects.length > 0 ? this.projects[0].id : null);
      if (!projectId) return;
      
      const projectIndex = this.projects.findIndex(p => p.id === projectId);
      if (projectIndex === -1) return;
      
      const extension = this.newFileDialog.name.split('.').pop().toLowerCase();
      
      let content = '';
      if (extension === 'quartz') {
        content = `// Arquivo Quartz: ${this.newFileDialog.name}\n// Criado em: ${new Date().toLocaleDateString()}\n\ncontract NovoContrato {\n  // Implemente seu contrato aqui\n}`;
      } else if (extension === 'md') {
        content = `# ${this.newFileDialog.name.split('.')[0]}\n\nDocumentação do projeto.`;
      }
      
      const newFile = {
        id: generateId(),
        name: this.newFileDialog.name,
        type: extension,
        content: content
      };
      
      this.projects[projectIndex].files.push(newFile);
      this.newFileDialog.show = false;
      
      // Selecionar o arquivo recém-criado
      this.selectFile(newFile);
    },
    
    renameFile(file) {
      if (!file) return;
      
      this.renameDialog.file = file;
      this.renameDialog.newName = file.name;
      this.renameDialog.show = true;
    },
    
    doRenameFile() {
      if (!this.renameDialog.file || !this.renameDialog.newName.trim()) return;
      
      const projectIndex = this.projects.findIndex(p => 
        p.files.some(f => f.id === this.renameDialog.file.id)
      );
      
      if (projectIndex === -1) return;
      
      const fileIndex = this.projects[projectIndex].files.findIndex(
        f => f.id === this.renameDialog.file.id
      );
      
      if (fileIndex === -1) return;
      
      // Atualizar o nome
      this.projects[projectIndex].files[fileIndex].name = this.renameDialog.newName;
      
      // Atualizar referência se necessário
      if (this.activeFile && this.activeFile.id === this.renameDialog.file.id) {
        this.activeFile = this.projects[projectIndex].files[fileIndex];
      }
      
      this.renameDialog.show = false;
    },
    
    confirmDeleteFile(file) {
      if (!file) return;
      
      this.deleteDialog.file = file;
      this.deleteDialog.show = true;
    },
    
    doDeleteFile() {
      if (!this.deleteDialog.file) return;
      
      const projectIndex = this.projects.findIndex(p => 
        p.files.some(f => f.id === this.deleteDialog.file.id)
      );
      
      if (projectIndex === -1) return;
      
      // Remover o arquivo
      this.projects[projectIndex].files = this.projects[projectIndex].files.filter(
        f => f.id !== this.deleteDialog.file.id
      );
      
      // Limpar referência se necessário
      if (this.activeFile && this.activeFile.id === this.deleteDialog.file.id) {
        this.activeFile = null;
        this.$emit('file-selected', null);
      }
      
      this.deleteDialog.show = false;
    }
  }
};
</script>

<style scoped>
.tree-project-explorer {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background-color: rgb(var(--v-theme-surface));
  border-radius: 4px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.projects-container {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.project-item {
  border-radius: 4px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  margin-bottom: 8px;
  overflow: hidden;
}

.project-header {
  background-color: rgba(var(--v-theme-surface-variant), 0.3);
  cursor: pointer;
}

.project-header:hover {
  background-color: rgba(var(--v-theme-surface-variant), 0.5);
}

.project-files {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 4px 0;
  background-color: rgba(var(--v-theme-background), 1);
}

.file-item {
  margin-bottom: 1px;
  border-radius: 2px;
}

.file-actions {
  display: none;
  opacity: 0;
  transition: opacity 0.2s;
}

.file-item:hover .file-actions {
  display: flex;
  opacity: 1;
}

.empty-state {
  height: 100%;
}

.fade-enter-active, .fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  max-height: 0;
}

.fade-enter-to, .fade-leave-from {
  opacity: 1;
  max-height: 500px;
}
</style>
